import { useEffect } from 'react';
import { FeatureGateEvaluationOptions, useFeatureGate } from '@statsig/react-bindings';
import { datadogRum } from '@datadog/browser-rum';

import { FeatureGates } from 'modules/common/constants/featuregates';
import { useGateOverride } from 'modules/common/integrations/Statsig/FeatureGateOverrides';

export type GateName = (typeof FeatureGates)[keyof typeof FeatureGates];
export type GateEvaluation = {
    name: GateName;
    value: boolean;
};

export default function useStatsigGate(
    gateName: GateName,
    evaluationOptions: FeatureGateEvaluationOptions = {},
    defaultValue = false
) {
    const gate = useFeatureGate(gateName, evaluationOptions);
    const resolveOverride = useGateOverride();
    const overrideValue = resolveOverride(gateName);
    const value = overrideValue ?? gate.value ?? defaultValue;

    // Stamps the value the member actually got onto RUM views and errors as `@feature_flags.<gate>`,
    // so Datadog can split any page's usage, errors or load time by gate. Optional call: telemetry must
    // never be able to break a render, including where the RUM module is stubbed without this method.
    useEffect(() => {
        datadogRum.addFeatureFlagEvaluation?.(gateName, value);
    }, [gateName, value]);

    return value;
}
